import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/** Canonical categories stored server-side — labels are resolved in UI. */
export const RECENT_SEARCH_CATEGORIES = {
  FLIGHTS: "flights",
  HOTELS: "hotels",
  VACATION_PACKAGES: "vacation_packages",
  ORGANIZED_TOURS: "organized_tours",
  DYNAMIC_PACKAGES: "dynamic_packages",
  CRUISE_PAYMENT: "cruise_payment",
  UNKNOWN: "unknown",
};

const TAB = {
  planes: "planes",
  hotels: "hotels",
  packages: "FlightsHotel",
  organizedTours: "OrganizedTour",
  dynamic: "DynamicPackages",
  cruise: "CruisePayment",
};

/** SearchContext `searchTab` → canonical category key */
export function canonicalCategoryFromSearchTab(searchTab) {
  switch (searchTab) {
    case TAB.planes:
      return RECENT_SEARCH_CATEGORIES.FLIGHTS;
    case TAB.hotels:
      return RECENT_SEARCH_CATEGORIES.HOTELS;
    case TAB.packages:
      return RECENT_SEARCH_CATEGORIES.VACATION_PACKAGES;
    case TAB.organizedTours:
      return RECENT_SEARCH_CATEGORIES.ORGANIZED_TOURS;
    case TAB.dynamic:
      return RECENT_SEARCH_CATEGORIES.DYNAMIC_PACKAGES;
    case TAB.cruise:
      return RECENT_SEARCH_CATEGORIES.CRUISE_PAYMENT;
    default:
      return RECENT_SEARCH_CATEGORIES.UNKNOWN;
  }
}

/** Hebrew titles for canonical categories */
export const RECENT_CATEGORY_LABEL_HE = {
  flights: "טיסות",
  hotels: "מלונות",
  vacation_packages: "חבילות נופש",
  organized_tours: "טיולים מאורגנים",
  // G.4E — historical recent-search rows only; product not active in UI.
  dynamic_packages: "חיפוש ישן",
  cruise_payment: "תשלום מנו",
  unknown: "חיפוש",
};

/** raw_payload.variant של חיפוש שבוצע בעמוד קמפיין */
export const BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT = "belvedere_campaign";
export const ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT = "royal_tulip_tbilisi_campaign";
export const ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT = "royal_zanzibar_campaign";
export const TEMBO_KIWENGWA_CAMPAIGN_RECENT_SEARCH_VARIANT =
  "tembo_kiwengwa_campaign";
export const ZANZIBELLA_CAMPAIGN_RECENT_SEARCH_VARIANT = "zanzibella_campaign";

const CAMPAIGN_RECENT_SEARCH_VARIANTS = new Set([
  BELVEDERE_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_TULIP_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ROYAL_ZANZIBAR_CAMPAIGN_RECENT_SEARCH_VARIANT,
  TEMBO_KIWENGWA_CAMPAIGN_RECENT_SEARCH_VARIANT,
  ZANZIBELLA_CAMPAIGN_RECENT_SEARCH_VARIANT,
]);

function readRawPayloadVariant(rawPayload) {
  if (rawPayload == null) return null;
  if (typeof rawPayload === "object" && !Array.isArray(rawPayload)) {
    return rawPayload.variant ?? null;
  }
  if (typeof rawPayload === "string") {
    try {
      const parsed = JSON.parse(rawPayload);
      return parsed && typeof parsed === "object" ? parsed.variant ?? null : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** Display label for a recent-search row — campaign searches show "קמפיין". */
export function recentSearchCategoryLabelHe(row) {
  const variant = readRawPayloadVariant(row?.raw_payload);
  if (CAMPAIGN_RECENT_SEARCH_VARIANTS.has(variant)) {
    return "קמפיין";
  }
  const cat = row?.category;
  return RECENT_CATEGORY_LABEL_HE[cat] || cat || "—";
}

export function isoDateFromDdMmYyyy(ddMmYyyy) {
  if (!ddMmYyyy || !String(ddMmYyyy).trim()) return null;
  const raw = String(ddMmYyyy).trim();
  const isoGuess = dayjs(raw, "YYYY-MM-DD", true);
  if (isoGuess.isValid()) return isoGuess.format("YYYY-MM-DD");
  const d = dayjs(raw, "DD/MM/YYYY", true);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
}

export function nightsFromDdMmYyyy(depDdMmOrIso, retDdMmOrIso) {
  const rawA = depDdMmOrIso != null ? String(depDdMmOrIso).trim() : "";
  const rawB = retDdMmOrIso != null ? String(retDdMmOrIso).trim() : "";
  if (!rawA || !rawB) return null;
  const pa = dayjs(rawA, "YYYY-MM-DD", true).isValid()
    ? dayjs(rawA, "YYYY-MM-DD", true)
    : dayjs(rawA, "DD/MM/YYYY", true);
  const pb = dayjs(rawB, "YYYY-MM-DD", true).isValid()
    ? dayjs(rawB, "YYYY-MM-DD", true)
    : dayjs(rawB, "DD/MM/YYYY", true);
  if (!pa.isValid() || !pb.isValid()) return null;
  const n = pb.diff(pa, "day");
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function tallyComposition(rooms) {
  let adults = 0;
  let children = 0;
  let infants = 0;
  if (!Array.isArray(rooms)) {
    return { adults, children, infants, rooms_count: 0 };
  }
  rooms.forEach((r) => {
    if (!r || typeof r !== "object") return;
    adults += Number(r.adults) || 0;
    children += Number(r.kids) || Number(r.children) || 0;
    infants += Number(r.infants) || 0;
  });
  return {
    adults,
    children,
    infants,
    rooms_count: rooms.length,
  };
}

/** Small JSON-safe snapshot (avoids cyclic structures). */
function snapshotLocation(lo) {
  if (!lo || typeof lo !== "object") return null;
  const keys = [
    "CITYCODE",
    "AIRPORTCODE",
    "CITYNAME",
    "CITYNAME_HEB",
    "COUNTRYNAME",
    "COUNTRYNAME_HEB",
    "fullname",
    "hotelserched",
    "HotelId",
    "hotelId",
    "place_id",
    "placeId",
    "hubwayzLocationId",
    "countryCode",
  ];
  const o = {};
  keys.forEach((k) => {
    const v = lo[k];
    if (v == null || v === "") return;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean")
      o[k] = v;
  });
  return Object.keys(o).length ? o : null;
}

function pickDestinationDisplay(primaryLocation) {
  const pl = primaryLocation;
  const label =
    (pl?.fullname != null ? String(pl.fullname).trim() : "") ||
    [pl?.CITYNAME_HEB, pl?.COUNTRYNAME_HEB].filter(Boolean).join(" ").trim() ||
    [pl?.CITYNAME, pl?.COUNTRYNAME].filter(Boolean).join(" ").trim() ||
    String(pl?.hotelserched || pl?.hotelSearched || "").trim();

  const code =
    String(pl?.CITYCODE || pl?.AIRPORTCODE || "").trim() || "";

  return { destination_label: label || null, destination_code: code || null };
}

/**
 * Builds POST /api/agent/recent-searches body fields (excluding agent — server-only).
 *
 * @param {object} p
 */
export function buildAgentRecentSearchPayload({
  searchTab,
  primaryLocation,
  location,
  origin,
  flightTripType,
  calendarDateArrival,
  calendarDateLeave,
  roomInfo,
  extraRaw = null,
  tourMonth = null,
}) {
  const primary = primaryLocation || location;
  const { destination_label, destination_code } = pickDestinationDisplay(primary);
  const { adults, children, infants, rooms_count } = tallyComposition(
    roomInfo?.rooms,
  );

  const category = canonicalCategoryFromSearchTab(searchTab);

  let departure_date = isoDateFromDdMmYyyy(calendarDateArrival);
  let return_date = isoDateFromDdMmYyyy(calendarDateLeave);
  let nights = nightsFromDdMmYyyy(calendarDateArrival, calendarDateLeave);

  let organizedTourMonthPayload = null;
  if (searchTab === TAB.organizedTours) {
    organizedTourMonthPayload =
      tourMonth != null && String(tourMonth).trim() ? String(tourMonth).trim() : null;
    const m = organizedTourMonthPayload
      ? dayjs(organizedTourMonthPayload, "MM/YYYY", true)
      : null;
    if (m && m.isValid()) {
      departure_date = m.startOf("month").format("YYYY-MM-DD");
    }
    return_date = null;
    nights = null;
  }

  const raw_payload = {
    v: 1,
    searchTab,
    flightTripType: flightTripType ?? null,
    calendarDateArrival: calendarDateArrival ?? null,
    calendarDateLeave: calendarDateLeave ?? null,
    location: snapshotLocation(location),
    primaryLocation: snapshotLocation(primary),
    origin: snapshotLocation(origin),
    roomInfoRooms: Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [],
    ...(extraRaw && typeof extraRaw === "object" ? extraRaw : {}),
    ...(organizedTourMonthPayload
      ? { tourMonth: organizedTourMonthPayload }
      : {}),
  };

  return {
    category,
    destination_label,
    destination_code,
    departure_date,
    return_date,
    nights,
    adults: adults || null,
    children: children || null,
    infants: infants || null,
    rooms_count: rooms_count || null,
    raw_payload,
  };
}

/**
 * Monthly grid hotel pricing search (distinct UI path).
 */
export function buildMonthlyHotelPricingRecentPayload({
  hotelId,
  hotelName,
  month,
  year,
  monthsCount,
  roomInfo,
}) {
  const tid = RECENT_SEARCH_CATEGORIES.HOTELS;
  const label = hotelName || (hotelId != null ? `Hotel ${hotelId}` : "");

  const { adults, children, infants, rooms_count } = tallyComposition(
    roomInfo?.rooms,
  );

  const raw_payload = {
    v: 1,
    variant: "monthly_hotel_lds_grid",
    hotelId: hotelId ?? null,
    hotelName: hotelName ?? null,
    month,
    year,
    monthsCount: monthsCount ?? 1,
    roomInfoRooms: Array.isArray(roomInfo?.rooms) ? roomInfo.rooms : [],
  };

  return {
    category: tid,
    destination_label: label || null,
    destination_code: hotelId != null ? String(hotelId) : null,
    departure_date: null,
    return_date: null,
    nights: null,
    adults: adults || null,
    children: children || null,
    infants: infants || null,
    rooms_count: rooms_count || null,
    raw_payload,
  };
}
